import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AccordionProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** e.g. icon action on the right; use `onClick` + `e.stopPropagation()` to avoid toggling the details. */
  action?: ReactNode;
  children: ReactNode;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  variant?: 'default' | 'soft' | 'plain' | 'fold' | 'inline';
  size?: 'md' | 'lg';
  className?: string;
  summaryClassName?: string;
  contentClassName?: string;
  /**
   * Passed to the native `details` element. Browsers only group (exclusive accordion) multiple
   * `details` that share the *same* non-empty `name`. Omitted or unique names keep sections independent.
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/details#name
   */
  name?: string;
}

const variantClasses = {
  default: 'rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30',
  soft: 'rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 shadow-sm',
  plain: 'rounded-none border-0 bg-transparent',
  /** Detail-page extras: own card on the page background, native-style disclosure. */
  fold: 'overflow-hidden rounded-[9px] border border-gray-200 bg-white shadow-sm shadow-gray-200/60 dark:border-gray-700 dark:bg-gray-800 dark:shadow-gray-900/50',
  /** Text row on the page background. The chevron follows the label. */
  inline: 'bg-transparent',
} as const;

const summaryClasses = {
  md: 'px-3 py-2.5 text-sm font-medium text-muted-strong',
  lg: 'px-4 py-3 text-sm font-medium text-foreground',
} as const;

const contentClasses = {
  md: 'space-y-3 px-3 pb-4 pt-3 border-t border-gray-200 dark:border-gray-700',
  lg: 'space-y-3 px-4 pb-4 pt-3 border-t border-gray-200 dark:border-gray-700',
  fold: 'px-[18px] pb-[18px] pt-0.5',
  inline: 'pb-1 pt-1',
} as const;

function AccordionMarker({
  fold,
  rotationClass,
  className,
}: {
  fold: boolean;
  rotationClass: string;
  className?: string;
}) {
  if (fold) {
    return (
      <span
        className={cn(
          'h-0 w-0 shrink-0 border-y-[5px] border-l-[7px] border-y-transparent border-l-current transition-transform',
          rotationClass,
        )}
        aria-hidden
      />
    );
  }

  return <ChevronRight className={cn('h-4 w-4 shrink-0 transition-transform', className, rotationClass)} aria-hidden />;
}

function accordionSummaryClass(isInline: boolean, isFold: boolean, size: 'md' | 'lg') {
  if (isInline) {
    return 'w-fit gap-1 rounded-md py-1.5 text-sm font-medium text-muted-strong transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30';
  }
  if (isFold) return 'justify-between px-[18px] py-3.5 text-sm font-semibold text-foreground';
  return cn('justify-between', summaryClasses[size]);
}

function accordionContentClass(isInline: boolean, isFold: boolean, size: 'md' | 'lg') {
  if (isInline) return contentClasses.inline;
  if (isFold) return contentClasses.fold;
  return contentClasses[size];
}

function accordionChevronClass(isControlled: boolean, open: boolean | undefined) {
  if (!isControlled) return 'group-open:rotate-90';
  if (open) return 'rotate-90';
  return '';
}

export function Accordion({
  title,
  subtitle,
  action,
  children,
  open,
  onToggle,
  variant = 'default',
  size = 'md',
  className,
  summaryClassName,
  contentClassName,
  name: detailsName,
}: AccordionProps) {
  const isControlled = open !== undefined;
  const isFold = variant === 'fold';
  const isInline = variant === 'inline';
  const chevronRotationClass = accordionChevronClass(isControlled, open);
  const showAside = (subtitle != null && subtitle !== false) || action;

  return (
    <details
      className={cn('group', variantClasses[variant], className)}
      name={detailsName && detailsName.length > 0 ? detailsName : undefined}
      open={open}
      onToggle={(e) => {
        if (isControlled) return;
        onToggle?.((e.currentTarget as HTMLDetailsElement).open);
      }}
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none select-none items-center gap-2 [&::-webkit-details-marker]:hidden',
          accordionSummaryClass(isInline, isFold, size),
          summaryClassName,
        )}
        onClick={(e) => {
          if (!isControlled || !onToggle) return;
          e.preventDefault();
          onToggle(!open);
        }}
      >
        <AccordionSummaryLabel title={title} isInline={isInline} isFold={isFold} rotationClass={chevronRotationClass} />
        {showAside ? (
          <div className='ml-2 sm:ml-3 flex items-center justify-end gap-1.5 sm:gap-2 shrink-0 min-w-0'>
            {subtitle ? <span className='text-xs text-muted truncate max-w-[min(220px,50vw)]'>{subtitle}</span> : null}
            {action}
          </div>
        ) : null}
      </summary>
      <div className={cn(accordionContentClass(isInline, isFold, size), contentClassName)}>{children}</div>
    </details>
  );
}

function AccordionSummaryLabel({
  title,
  isInline,
  isFold,
  rotationClass,
}: {
  title: ReactNode;
  isInline: boolean;
  isFold: boolean;
  rotationClass: string;
}) {
  if (isInline) {
    return (
      <>
        <span className='min-w-0'>{title}</span>
        <AccordionMarker fold={false} rotationClass={rotationClass} className='h-3.5 w-3.5' />
      </>
    );
  }

  return (
    <span className={cn('inline-flex min-w-0 flex-1 items-center', isFold ? 'gap-2.5' : 'gap-2')}>
      <AccordionMarker fold={isFold} rotationClass={rotationClass} />
      <span className='min-w-0'>{title}</span>
    </span>
  );
}
